import React, { useState, useEffect } from 'react';
import { X, Plus, Edit, Trash2, Save, Percent, Package, Wrench, Square, DoorOpen } from 'lucide-react';
import materialPricingWeightsService from '../services/materialPricingWeightsService';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const MaterialPricingWeightsManager = ({ onClose }) => {
  const [weights, setWeights] = useState([]);
  const [groupedWeights, setGroupedWeights] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingWeight, setEditingWeight] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [formData, setFormData] = useState({
    preference_type: '',
    option_key: '',
    option_label: '',
    weight_percentage: 0,
    description: '',
    display_order: 0,
    is_active: true
  });

  const preferenceTypes = {
    material_type: {
      label: 'Tipo de Material',
      icon: Package,
      options: [
        { key: '1', label: 'Tipo 1' },
        { key: '2', label: 'Tipo 2' },
        { key: '3', label: 'Tipo 3' }
      ]
    },
    hardware_type: {
      label: 'Tipo de Ferragens',
      icon: Wrench,
      options: [
        { key: '1', label: 'Tipo 1' },
        { key: '2', label: 'Tipo 2' },
        { key: '3', label: 'Tipo 3' }
      ]
    },
    has_glass: {
      label: 'Com Vidro',
      icon: Square,
      options: [
        { key: 'false', label: 'Sem Vidro' },
        { key: 'true', label: 'Com Vidro' }
      ]
    },
    door_finish: {
      label: 'Tipo de Acabamento para Portas',
      icon: DoorOpen,
      options: [
        { key: 'Liso', label: 'Liso' },
        { key: 'Texturizado', label: 'Texturizado' },
        { key: 'Lacado', label: 'Lacado' },
        { key: 'Verniz', label: 'Verniz' },
        { key: 'Outro', label: 'Outro' }
      ]
    }
  };

  useEffect(() => {
    loadWeights();
  }, []);

  const loadWeights = async () => {
    try {
      setLoading(true);
      const response = await materialPricingWeightsService.getAllWeights({ is_active: true });
      
      if (response.grouped) {
        setGroupedWeights(response.grouped);
        setWeights(response.data || []);
      } else {
        // Agrupar manualmente se não vier agrupado
        const grouped = {};
        (response.data || []).forEach(weight => {
          if (!grouped[weight.preference_type]) {
            grouped[weight.preference_type] = [];
          }
          grouped[weight.preference_type].push(weight);
        });
        setGroupedWeights(grouped);
        setWeights(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar pesos:', error);
      toast.error('Erro ao carregar pesos: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (weight) => {
    setEditingWeight(weight);
    setFormData({
      id: weight.id,
      preference_type: weight.preference_type,
      option_key: weight.option_key,
      option_label: weight.option_label,
      weight_percentage: weight.weight_percentage,
      description: weight.description || '',
      display_order: weight.display_order || 0,
      is_active: weight.is_active
    });
    setShowForm(true);
  };

  const handleAdd = (preferenceType, option) => {
    // Verificar se já existe
    const existing = weights.find(
      w => w.preference_type === preferenceType && 
           w.option_key === option.key && 
           w.is_active
    );

    if (existing) {
      handleEdit(existing);
      return;
    }

    setEditingWeight(null);
    setFormData({
      preference_type: preferenceType,
      option_key: option.key,
      option_label: option.label,
      weight_percentage: 0,
      description: '',
      display_order: 0,
      is_active: true
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingWeight) {
        await materialPricingWeightsService.upsertWeight(formData);
        toast.success('Peso atualizado com sucesso!');
      } else {
        await materialPricingWeightsService.upsertWeight(formData);
        toast.success('Peso criado com sucesso!');
      }
      
      setShowForm(false);
      setEditingWeight(null);
      resetForm();
      loadWeights();
    } catch (error) {
      console.error('Erro ao salvar peso:', error);
      toast.error('Erro ao salvar peso: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    setConfirmAction({ id, type: 'delete' });
    setShowConfirmModal(true);
  };

  const executeConfirmedAction = async () => {
    if (!confirmAction) return;

    const { id, type } = confirmAction;

    if (type === 'delete') {
      try {
        await materialPricingWeightsService.deleteWeight(id);
        toast.success('Peso desativado com sucesso!');
        loadWeights();
      } catch (error) {
        console.error('Erro ao deletar peso:', error);
        toast.error('Erro ao deletar peso: ' + (error.response?.data?.message || error.message));
      }
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  const resetForm = () => {
    setFormData({
      preference_type: '',
      option_key: '',
      option_label: '',
      weight_percentage: 0,
      description: '',
      display_order: 0,
      is_active: true
    });
  };

  const getWeightForOption = (preferenceType, optionKey) => {
    return weights.find(
      w => w.preference_type === preferenceType && 
           w.option_key === optionKey && 
           w.is_active
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Pesos de Preferências de Material</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure os pesos (porcentagens) que cada opção adiciona ao valor final do orçamento
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Lista de Pesos por Tipo */}
      {Object.entries(preferenceTypes).map(([type, config]) => {
        const Icon = config.icon;
        const typeWeights = groupedWeights[type] || [];

        return (
          <div key={type} className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Icon className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{config.label}</h3>
            </div>

            <div className="space-y-3">
              {config.options.map((option) => {
                const weight = getWeightForOption(type, option.key);
                
                return (
                  <div
                    key={option.key}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900">{option.label}</span>
                        {weight ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-sm">
                            <Percent className="w-3 h-3" />
                            {weight.weight_percentage > 0 ? '+' : ''}{weight.weight_percentage}%
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">Não configurado</span>
                        )}
                      </div>
                      {weight?.description && (
                        <p className="text-xs text-gray-600 mt-1">{weight.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {weight ? (
                        <>
                          <button
                            onClick={() => handleEdit(weight)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1"
                          >
                            <Edit className="w-4 h-4" />
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(weight.id)}
                            className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center gap-1"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remover
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleAdd(type, option)}
                          className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm flex items-center gap-1"
                        >
                          <Plus className="w-4 h-4" />
                          Configurar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Modal de Formulário */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingWeight ? 'Editar Peso' : 'Configurar Peso'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingWeight(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Preferência
                </label>
                <input
                  type="text"
                  value={preferenceTypes[formData.preference_type]?.label || formData.preference_type}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opção
                </label>
                <input
                  type="text"
                  value={formData.option_label}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Peso em Porcentagem (%) *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={formData.weight_percentage}
                    onChange={(e) => setFormData({ ...formData, weight_percentage: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="0.00"
                    required
                  />
                  <div className="absolute right-3 top-2.5 text-gray-500">
                    <Percent className="w-5 h-5" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Este valor será aplicado como porcentagem adicional ao valor base do orçamento.
                  Pode ser positivo (aumenta) ou negativo (diminui).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição (opcional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  rows="3"
                  placeholder="Descrição adicional sobre este peso..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordem de Exibição
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  min="0"
                />
              </div>

              <div className="flex items-center gap-2 pt-4 border-t">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingWeight ? 'Atualizar' : 'Salvar'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingWeight(null);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <ConfirmModal
          title="Confirmar Desativação"
          message="Tem certeza que deseja desativar este peso?"
          onConfirm={executeConfirmedAction}
          onCancel={() => {
            setShowConfirmModal(false);
            setConfirmAction(null);
          }}
        />
      )}
    </div>
  );
};

export default MaterialPricingWeightsManager;
